const MIME_PADRAO = 'application/octet-stream'

// O browser lê o Blob de forma assíncrona depois do clique, e não existe evento
// que avise que o download terminou de ler os bytes. Revogar junto com o clique
// arrisca puxar o conteúdo debaixo de um download em curso, e o risco cresce com
// o tamanho do arquivo — justamente o caso desta função. Os vizinhos
// `downloadCsv`/`downloadTxt` podem revogar na hora porque servem poucos KB de
// texto montados em memória; aqui trafega o PDF de 8 MB. 40 s é a mesma janela
// que o `jspdf` usa (`4e4`, `node_modules/jspdf/dist/jspdf.es.js:176`): folgada
// para o download começar, curta para não segurar dezenas de MB na aba.
const MS_ATE_REVOGAR_URL = 40000

// Extensões que o backend entrega hoje pelo contrato base64
// (`FileHandler.send_file_to_front_base_64()`). O tipo certo faz o arquivo salvo
// abrir no app certo; extensão fora desta lista mantém `octet-stream`, que era o
// tipo único usado antes — quem dependia do comportamento anterior não muda.
const MIME_POR_EXTENSAO = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
  xml: 'application/xml',
  json: 'application/json',
  zip: 'application/zip',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
}

// Alguns chamadores passam a extensão com ponto (`'.csv'`, em apiCrmBatchImport).
const mimeDaExtensao = (extension) => {
  const chave = String(extension || '')
    .trim()
    .toLowerCase()
    .replace(/^\./, '')

  return MIME_POR_EXTENSAO[chave] || MIME_PADRAO
}

// Tolera o data URI completo caso algum chamador já monte o prefixo — o alfabeto
// base64 não tem `:`, então não há como um payload legítimo casar aqui.
const PREFIXO_DATA_URI = /^data:[^,]*;base64,/

// A Gmail API devolve o anexo em base64url e `api_google_read_emails.py:318`
// repassa cru para o front. O alfabeto url-safe troca `+`/`/` por `-`/`_`, que
// o `atob` recusa: medido, um anexo de 1 KB tem 100% de chance de conter um
// deles, então o download de anexo do CRM falhava para praticamente qualquer
// arquivo. Normalizar aqui conserta os consumidores de uma vez; os dois
// alfabetos não se sobrepõem, então a troca nunca corrompe base64 padrão.
const paraAlfabetoPadrao = (base64) =>
  base64.replace(/-/g, '+').replace(/_/g, '/')

const base64ParaBytes = (base64) => {
  let binario

  // `window.atob('')` é válido e devolve string vazia, então conteúdo ausente
  // escapava da validação de alfabeto abaixo e virava um arquivo de 0 byte
  // salvo na máquina do usuário — a mesma falha silenciosa que esta função
  // combate, só que com o arquivo quebrado no disco em vez de nenhum arquivo.
  // Medido no navegador: com o `data:` URI antigo o Chromium cancelava o
  // download nesse caso. `base64` ausente na resposta é o input inválido mais
  // provável, então vale barrar antes e com a mesma mensagem diagnóstica.
  if (!base64.length) {
    throw new Error(
      'Base64 vazio recebido do backend: nenhum conteúdo para baixar. ' +
        'Esperado: o conteúdo do arquivo em base64 no campo `base64` da ' +
        'resposta. Confira se a rota devolveu o arquivo de fato.'
    )
  }

  try {
    binario = window.atob(paraAlfabetoPadrao(base64))
  } catch (erro) {
    // Base64 quebrado vinha do `data:` URI como arquivo corrompido em silêncio:
    // o usuário salvava lixo achando que era o relatório. Falhar alto é melhor,
    // mas só ajuda se a mensagem disser de onde veio o defeito — o conteúdo é
    // produzido pelo backend, não por quem chamou o download.
    throw new Error(
      'Base64 inválido recebido do backend: ' +
        `${base64.length} caracteres, começando em "${base64.slice(0, 32)}". ` +
        'Esperado: alfabeto base64 padrão (A-Z a-z 0-9 + /), ' +
        'com comprimento múltiplo de 4 e padding "=". ' +
        `Erro original: ${erro.message}`
    )
  }

  const bytes = new Uint8Array(binario.length)

  for (let i = 0; i < binario.length; i += 1) {
    bytes[i] = binario.charCodeAt(i)
  }

  return bytes
}

/**
 * Função para realizar o download de arquivos por base64.
 * @param {String} base64 Codigo em base64 representando o arquivo
 * @param {String} name_file Nome do arquivo que será baixado
 * @param {String} extension Extensão/Formato do arquivo
 */
export const downloadBase64 = (base64, name_file, extension) => {
  // O `data:` URI que ficava aqui embutia o arquivo INTEIRO no endereço: um PDF
  // de 8,26 MB vira ~11 MB de base64, isto é, uma URL de 11 milhões de
  // caracteres — acima do teto que os browsers aceitam. Passado esse teto o
  // `click()` simplesmente não fazia nada: sem download, sem erro no console e
  // sem mensagem, então o usuário via a barra completar e nenhum arquivo
  // aparecer. Com Blob os bytes ficam fora da URL, que vira um ponteiro curto, e
  // o tamanho do arquivo deixa de ser um limite.
  const conteudo = String(base64 || '')
    .replace(PREFIXO_DATA_URI, '')
    .replace(/\s/g, '')

  const blob = new Blob([base64ParaBytes(conteudo)], {
    type: mimeDaExtensao(extension),
  })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `${name_file}.${extension}`
  a.click()

  setTimeout(() => URL.revokeObjectURL(url), MS_ATE_REVOGAR_URL)
}

const CSV_SEPARATOR = ';'
// BOM UTF-8: garante que Excel/Bloco de Notas renderizem acentos corretamente
// ao abrir o arquivo. Serve tanto o export CSV quanto o export TXT abaixo.
const UTF8_BOM = '﻿'
const TXT_MIME = 'text/plain;charset=utf-8;'

const escapeCsvCell = (value) => {
  const cell = value === null || value === undefined ? '' : String(value)
  const needsQuotes =
    cell.includes(CSV_SEPARATOR) ||
    cell.includes('"') ||
    cell.includes('\n') ||
    cell.includes('\r')

  if (!needsQuotes) return cell
  return `"${cell.replace(/"/g, '""')}"`
}

/**
 * Gera e baixa um arquivo CSV (compatível com Excel) a partir de dados em memória.
 * Útil para exportar listas já carregadas no front sem depender de endpoint backend.
 * Usa separador `;` (padrão pt-BR no Excel) e BOM UTF-8 para preservar acentos.
 * @param {Object} params
 * @param {Array<Object>} params.headers Colunas no formato `{ text, value }`
 * @param {Array<Object>} params.rows Linhas de dados (objetos com as chaves de `value`)
 * @param {String} params.fileName Nome do arquivo (sem extensão)
 */
export const downloadCsv = ({
  headers = [],
  rows = [],
  fileName = 'export',
} = {}) => {
  const headerLine = headers
    .map((header) => escapeCsvCell(header.text))
    .join(CSV_SEPARATOR)

  const dataLines = rows.map((row) =>
    headers
      .map((header) => escapeCsvCell(row[header.value]))
      .join(CSV_SEPARATOR)
  )

  const content = UTF8_BOM + [headerLine].concat(dataLines).join('\r\n')
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `${fileName}.csv`
  a.click()

  URL.revokeObjectURL(url)
}

/**
 * Gera e baixa um arquivo de texto plano a partir de conteúdo já montado em memória.
 * Irmão de `downloadCsv`: mesma mecânica de Blob, mesmo BOM UTF-8.
 * @param {Object} params
 * @param {String} params.content Conteúdo completo do arquivo (já com quebras de linha)
 * @param {String} params.fileName Nome do arquivo (sem extensão)
 */
export const downloadTxt = ({ content = '', fileName = 'export' } = {}) => {
  const blob = new Blob([UTF8_BOM + content], { type: TXT_MIME })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `${fileName}.txt`
  a.click()

  URL.revokeObjectURL(url)
}
